import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { Button } from 'components/Form/Button/Button';
import { InfoPopoverBody, InfoPopoverFooter } from 'components/InfoPopover/InfoPopover';
import { useTranslation } from 'react-i18next';
import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { classNames } from 'support/helpers/generic/generic';
import { InvoiceDiscrepancy } from 'services/invoiceDiscrepancies/types';
import { PriceDiscrepancyView } from './PriceDiscrepancyView';
import { TaxDiscrepancyView } from './TaxDiscrepancyView';
import { QuantityDiscrepancyView } from './QuantityDiscrepancyView';
import { ChargeDiscrepancyView } from './ChargeDiscrepancyView';
import { AllowanceDiscrepancyView } from './AllowanceDiscrepancyView';

export const WithErrorsContent = ({ error, numberOfErrors }: { error: InvoiceDiscrepancy; numberOfErrors: number }) => {
  const { t } = useTranslation();

  const { service } = useErrorContext<InvoiceDiscrepancy>();
  const showNextDiscrepancyButton = numberOfErrors > 1;

  const renderDiscrepancyContent = () => {
    switch (error.type) {
      case 'prices':
        return (
          <PriceDiscrepancyView
            data={error.data}
            relatedTransactions={error.relatedTransactions}
            currency={error.currency}
          />
        );
      case 'taxes':
        return <TaxDiscrepancyView data={error.data} relatedTransactions={error.relatedTransactions} />;
      case 'quantities':
        return <QuantityDiscrepancyView data={error.data} />;
      case 'charges':
        return <ChargeDiscrepancyView data={error.data} currency={error.currency} />;
      case 'allowances':
        return <AllowanceDiscrepancyView data={error.data} currency={error.currency} />;
      default:
        return null;
    }
  };

  return (
    <>
      <InfoPopoverBody>
        <div className="flex justify-between gap-2">
          <h1 className="text-sm font-bold text-red-700">
            {t(`invoiceVerification:detailsPage.discrepanciesModal.title.${error.type}`)}
          </h1>
        </div>
        <div className="pt-3 text-sm text-gray-700">{renderDiscrepancyContent()}</div>
      </InfoPopoverBody>
      <InfoPopoverFooter>
        <div></div> {/* This empty div is to align the buttons to the right */}
        <Button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={service.moveToNextError}
          variant="minimal-danger"
          RightIcon={ChevronRightIcon}
          size="extra-small"
          className={classNames(showNextDiscrepancyButton ? 'visible' : 'invisible')}
          analyticsId="invoiceVerification:detailsPage.discrepanciesModal.nextDiscrepancy"
        >
          {t('invoiceVerification:detailsPage.discrepanciesModal.nextDiscrepancyButton')}
        </Button>
      </InfoPopoverFooter>
    </>
  );
};

/*
 * i18n keys
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.quantities')
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.prices')
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.taxes')
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.charges')
 * t('invoiceVerification:detailsPage.discrepanciesModal.title.allowances')
 */
